/**
 * Utils do modal genérico de edição de campo do painel de resumo
 * (lead-summary-card + crm-edit-field-modal).
 *
 * 1) Registry de campos editáveis — cada campo do summary (`field_key` do
 *    catalog) que faz sentido editar inline aponta pra um descritor
 *    `{ inputType, saveStrategy, payloadKey, optionsSource }`. O card e o
 *    modal consultam esse registry pra decidir: (1) mostra lápis de
 *    editar? (2) qual input renderizar? (3) qual API chamar ao salvar?
 *
 *    Fora do registry (display-only, nunca editável por aqui):
 *      - Computados: pl_total, saldo_em_conta, rent_ano, receita_mes,
 *        segmentacao_por_pl.
 *      - `tags` já tem edição inline própria (ClientTags).
 *      - Dividers (`field_source: 'divider'`) são só visuais.
 *      - `taxa_administracao`/`taxa_performance` continuam no fluxo do
 *        `crm-edit-fee-model` (seletor de modelo de cobrança, não é um
 *        input simples de 1 campo — fora do escopo deste registry).
 *      - `id_profissional_responsavel` não existe no catalog do summary
 *        (não tem ícone em `summaryAdapters.ICON_BY_FIELD_KEY`), logo não
 *        é alcançável clicando no painel — não faz sentido incluir aqui.
 *
 * 2) `resolveFieldOptions` — resolve a lista de opções de um campo
 *    select-search a partir do `OPTIONS_SOURCE` do descritor. Extraído de
 *    `crm-edit-field-modal.vue` (eram 4 métodos privados) pra ser
 *    reaproveitado também pelo `crm-required-fields-modal.vue` — os dois
 *    modais resolvem opções da mesma forma, só divergem no que fazem com
 *    o valor atual depois (o modal de campo único remapeia origem
 *    label->id a partir do valor exibido no painel; o modal de campos
 *    obrigatórios sempre começa vazio, não precisa remapear nada).
 *
 * 3) Payload builder pro patch de dados cadastrais
 *    (`/v2/api/professional/client/data/edit`) — extraído da classe de API
 *    (`apiCrmClientDetails.saveClientFields`) pra ficar testável sem
 *    mockar `con`/`connection.js`.
 *
 *    IMPORTANTE (ver crm-edit-field-modal.vue `saveClientData`): a rota
 *    faz `sql_handler.update(AppClientes, dic_values=dic_data_parsed,
 *    ...)` — se NENHUMA chave do payload estiver na whitelist
 *    `FIELDS_CLIENT_APP` do backend (ex: rg, estado_civil, profissao,
 *    cep, endereco*, bairro, cidade, estado — não whitelistados),
 *    `dic_data_parsed` vira `{}` e o UPDATE crasha com 500 (`dic_values
 *    must be not empty dict`). Por isso o modal SEMPRE inclui um
 *    campo-âncora (`nome_cliente`, inalterado) junto do campo editado —
 *    garante que o patch nunca fica vazio pro backend, e ainda assim o
 *    dado bruto completo chega em
 *    `APIAppClientesDadosCadastrais.add_or_update(data=dic_data, ...)`
 *    (mesma rota, chamada depois, sem o filtro de whitelist), que é onde
 *    esses campos "extra" são de fato persistidos.
 */

import store from '@state/store'
import { apiCrmOrigem } from '@modules/crm/api/apiCrmOrigem'
import {
  ESTADO_CIVIL,
  OPPORTUNITY_TEMPERATURE_NAMES,
} from '@modules/crm/constants/Text'

// ============================================================
// 1) Registry de campos editáveis
// ============================================================

// Estratégias de save. O modal genérico faz um switch nesse valor.
export const SAVE_STRATEGY = {
  CLIENT_DATA: 'clientData',
  LEAD: 'lead',
  OBSERVACAO: 'observacao',
  CUSTOM: 'custom',
}

// Fontes de opções pra campos select. Resolvidas pelo modal (cada uma lê de
// um getter/const diferente) — o registry só declara qual usar.
export const OPTIONS_SOURCE = {
  ESTADO_CIVIL: 'estadoCivil',
  PERFIL: 'perfil',
  ORIGEM: 'origem',
  TEMPERATURA: 'temperatura',
}

// field_key (catalog) -> descritor de edição. `payloadKey` é a chave que a
// API de destino espera (quando difere do field_key do summary).
const CLIENT_DATA_FIELDS = {
  nome: { inputType: 'text', payloadKey: 'nome_cliente' },
  cpf: { inputType: 'cpf' },
  rg: { inputType: 'text' },
  data_nascimento: { inputType: 'date' },
  estado_civil: {
    inputType: 'select-search',
    optionsSource: OPTIONS_SOURCE.ESTADO_CIVIL,
  },
  profissao: { inputType: 'text' },
  email: { inputType: 'email' },
  telefone: { inputType: 'telefone' },
  cep: { inputType: 'cep' },
  endereco: { inputType: 'text' },
  endereco_numero: { inputType: 'text' },
  complemento: { inputType: 'text' },
  bairro: { inputType: 'text' },
  cidade: { inputType: 'text' },
  estado: { inputType: 'text' },
  perfil: { inputType: 'select-search', optionsSource: OPTIONS_SOURCE.PERFIL },
  pl_potencial: { inputType: 'money' },
  codigo_corretora: { inputType: 'text' },
  qualificado: { inputType: 'checkbox' },
  cliente_pj: { inputType: 'checkbox' },
  data_inicio_fee: { inputType: 'date' },
  origem: {
    inputType: 'select-search',
    payloadKey: 'id_origem',
    optionsSource: OPTIONS_SOURCE.ORIGEM,
  },
}

// `descricao` (campo do lead, crm_lead.descricao) sempre salva via
// apiCrmLeads.salvar — igual ao modal que ela substitui
// (crm-edit-description-modal), que nunca olhou pro entityType.
const LEAD_FIELDS = {
  temperatura: {
    inputType: 'select-search',
    optionsSource: OPTIONS_SOURCE.TEMPERATURA,
  },
  valor_estimado: { inputType: 'money' },
  data_fechamento_prevista: { inputType: 'date' },
  descricao: { inputType: 'textarea-new-ux' },
}

// `observacao` é um campo à parte de `descricao` — nota livre do cliente,
// editada hoje pelo widget `crm-fixed-comment`, salva via
// apiCrmObservacoes.salvar (mantém o cache próprio `crm/observacaoClient`
// em sync). Diferente de `descricao` (que é do lead).
const OBSERVACAO_FIELDS = {
  observacao: { inputType: 'textarea-new-ux' },
}

/**
 * Resolve o descritor de edição de um field_key do summary.
 *
 * @param {Object} params
 * @param {string} params.fieldKey - catalog key (ex: 'nome', 'perfil')
 * @param {Object} [params.catalogMeta] - entrada do catalog pra esse field_key
 *   (`catalogByKey[fieldKey]`), usada só pra detectar campo personalizado.
 * @returns {Object|null} descritor `{ fieldKey, label, inputType,
 *   saveStrategy, payloadKey, optionsSource }` ou `null` se o campo não é
 *   editável por aqui.
 */
export function getEditDescriptor({ fieldKey, catalogMeta } = {}) {
  if (!fieldKey) return null

  // Campo personalizado: detectado dinamicamente pelo catalog (mesmo check
  // usado em summary-layout-form.js), nunca por uma lista fixa de keys —
  // o conjunto de campos personalizados é definido por escritório.
  if (catalogMeta && catalogMeta.handler === 'custom_fields') {
    return {
      fieldKey,
      label: catalogMeta.label || fieldKey,
      saveStrategy: SAVE_STRATEGY.CUSTOM,
    }
  }

  if (Object.hasOwn(OBSERVACAO_FIELDS, fieldKey)) {
    return {
      fieldKey,
      label: (catalogMeta && catalogMeta.label) || fieldKey,
      ...OBSERVACAO_FIELDS[fieldKey],
      saveStrategy: SAVE_STRATEGY.OBSERVACAO,
    }
  }

  if (Object.hasOwn(LEAD_FIELDS, fieldKey)) {
    return {
      fieldKey,
      label: (catalogMeta && catalogMeta.label) || fieldKey,
      ...LEAD_FIELDS[fieldKey],
      saveStrategy: SAVE_STRATEGY.LEAD,
    }
  }

  if (Object.hasOwn(CLIENT_DATA_FIELDS, fieldKey)) {
    return {
      fieldKey,
      label: (catalogMeta && catalogMeta.label) || fieldKey,
      ...CLIENT_DATA_FIELDS[fieldKey],
      saveStrategy: SAVE_STRATEGY.CLIENT_DATA,
    }
  }

  return null
}

// ============================================================
// 2) Opções de campos select-search
// ============================================================

/**
 * Resolve a lista de opções de um `OPTIONS_SOURCE`. Só devolve as opções
 * cruas — não mexe em valor atual/seleção (isso é responsabilidade de
 * quem chama, já que cada modal trata o valor atual de forma diferente).
 *
 * @param {string} source - um dos `OPTIONS_SOURCE`.
 * @returns {Promise<Array>} lista de opções (formato varia por source —
 *   mesmo formato que `<input-by-type type="select-search">` já espera).
 */
export async function resolveFieldOptions(source) {
  if (source === OPTIONS_SOURCE.ESTADO_CIVIL) {
    return ESTADO_CIVIL
  }

  if (source === OPTIONS_SOURCE.TEMPERATURA) {
    return Object.keys(OPPORTUNITY_TEMPERATURE_NAMES).map((key) => ({
      id: key,
      value: OPPORTUNITY_TEMPERATURE_NAMES[key],
    }))
  }

  if (source === OPTIONS_SOURCE.PERFIL) {
    const perfis = store.getters['portfolio/portfolioModels'] || []
    return perfis.map((p) => p.nome)
  }

  if (source === OPTIONS_SOURCE.ORIGEM) {
    await apiCrmOrigem.carregar()
    const origens = store.getters['crm/origens'] || []
    return origens.map((o) => ({ ...o }))
  }

  return []
}

// ============================================================
// 3) Payload builder — patch de dados cadastrais
// ============================================================

/**
 * @param {Object} params
 * @param {number|string} params.idClienteApp
 * @param {Object} params.fields - mapa { chave: valor } esperado pela API
 *   (ex: { estado_civil: 'Casado(a)', nome_cliente: 'João' })
 * @returns {{ error: null, payload: Object }|{ error: Object, payload: null }}
 *   `payload` pronto pra `con.post(url, payload)`, ou `error` (status 400)
 *   quando os parâmetros obrigatórios estão ausentes.
 */
export function buildClientFieldsPatchPayload({ idClienteApp, fields }) {
  if (!idClienteApp || !fields || !Object.keys(fields).length) {
    return {
      error: { status: 400, message: 'Dados inválidos.' },
      payload: null,
    }
  }

  return {
    error: null,
    payload: {
      id_cliente_app: idClienteApp,
      dados_cliente: JSON.stringify(fields),
    },
  }
}
